import { getProviderJson, postProviderJson, providerError } from './providerHttp';
import { streamProviderSse, canStream } from '../providerStream';

/**
 * Google Gemini (generateContent) adapter (BYO key).
 *
 * Translates the app's Anthropic-shaped history (the internal lingua
 * franca) to Gemini's contents format and back to the shared callback
 * surface:
 *
 *   Anthropic                      →  Gemini
 *   system string                  →  systemInstruction
 *   tools input_schema             →  functionDeclarations.parameters
 *                                     (sanitized — see sanitizeSchema)
 *   user string content            →  {role:'user', parts:[{text}]}
 *   assistant text + tool_use      →  {role:'model', parts:[{text},{functionCall}]}
 *   user tool_result blocks        →  {role:'user', parts:[{functionResponse}]}
 *
 * Gemini function calls carry NO id — only a name. The Anthropic shape the
 * panel speaks requires ids (tool_result references tool_use_id), so this
 * adapter SYNTHESIZES an id per call on the way in, and on the way out maps
 * a tool_result's id back to the function NAME by scanning the history's own
 * assistant tool_use blocks. The id never reaches Google; it only has to be
 * stable within the client's history, which the scan guarantees.
 *
 * Non-streamed on purpose — see providerHttp.js.
 */

const API_BASE = 'https://generativelanguage.googleapis.com/v1beta/models';

function url(model) {
  return `${API_BASE}/${encodeURIComponent(model)}:generateContent`;
}

/** Same model, streamed. `alt=sse` is what makes it `data:` frames rather than
 *  a JSON array, which is the only form the shared SSE transport can read. */
function streamUrl(model) {
  return `${API_BASE}/${encodeURIComponent(model)}:streamGenerateContent?alt=sse`;
}

function headers(apiKey) {
  return { 'x-goog-api-key': apiKey };
}

// JSON-Schema keys Gemini's OpenAPI-subset schema accepts. Everything else —
// notably additionalProperties, which several of our tool schemas use — makes
// the request 400, so schemas are rebuilt from an allowlist rather than
// patched by deleting known offenders.
const SCHEMA_KEYS = ['type', 'description', 'enum', 'properties', 'required', 'items', 'nullable', 'format'];

export function sanitizeSchema(schema) {
  if (!schema || typeof schema !== 'object') return schema;
  const out = {};
  for (const key of SCHEMA_KEYS) {
    if (!(key in schema)) continue;
    if (key === 'properties') {
      out.properties = {};
      for (const [name, sub] of Object.entries(schema.properties || {})) {
        out.properties[name] = sanitizeSchema(sub);
      }
    } else if (key === 'items') {
      out.items = sanitizeSchema(schema.items);
    } else {
      out[key] = schema[key];
    }
  }

  // An OPEN MAP — `{type:'object', additionalProperties:{...}}`, which is how
  // `headers` is declared on send_request, create_request and update_request.
  // `additionalProperties` is not in the allowlist because Gemini rejects it,
  // and stripping it leaves a bare `{"type":"object"}`: an object with no
  // declared properties, which tells the model nothing about what may go in
  // it (and which Gemini's OpenAPI subset is entitled to reject outright).
  //
  // Gemini has no way to express a free-form map, so declare the honest
  // alternative — a JSON string — and convert the value back on the way in
  // (see coerceOpenMapArgs). Anthropic and OpenAI keep the real map.
  if (out.type === 'object' && !Object.keys(out.properties || {}).length
      && schema.additionalProperties) {
    return {
      type: 'string',
      description: `${out.description ? `${out.description} ` : ''}Provide as a JSON object string, e.g. {"Content-Type":"application/json"}.`,
      __openMap: true,
    };
  }

  return out;
}

/** Names of the fields sanitizeSchema turned into JSON strings, per tool. */
function mapFieldsFor(tools) {
  const byTool = {};
  for (const tool of tools || []) {
    const props = tool.input_schema?.properties || {};
    const fields = Object.entries(props)
      .filter(([, sub]) => sub?.type === 'object' && sub.additionalProperties)
      .map(([name]) => name);
    if (fields.length) byTool[tool.name] = fields;
  }
  return byTool;
}

/**
 * Turn a JSON-string argument back into the object the executor expects.
 *
 * Only for fields this adapter rewrote. A model that ignores the instruction
 * and sends a real object still works — the parse is attempted, not required.
 */
function coerceOpenMapArgs(name, args, mapFields) {
  const fields = mapFields[name];
  if (!fields || !args) return args;

  const out = { ...args };
  for (const field of fields) {
    const value = out[field];
    if (typeof value !== 'string') continue;
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) out[field] = parsed;
    } catch {
      // Leave it as-is: a malformed map is the executor's error to report,
      // not something to silently drop.
    }
  }
  return out;
}

export function toFunctionDeclarations(tools) {
  return (tools || []).map((t) => {
    const decl = { name: t.name, description: t.description };
    const schema = t.input_schema || {};
    // An empty-properties object schema is what our no-arg tools carry;
    // Gemini prefers `parameters` omitted entirely for those.
    if (schema.properties && Object.keys(schema.properties).length > 0) {
      decl.parameters = stripMarkers(sanitizeSchema(schema));
    }
    return decl;
  });
}

/** Remove the internal `__openMap` marker — Gemini must never see it. */
function stripMarkers(node) {
  if (!node || typeof node !== 'object') return node;
  const { __openMap: _ignored, ...rest } = node;
  if (rest.properties) {
    rest.properties = Object.fromEntries(
      Object.entries(rest.properties).map(([k, v]) => [k, stripMarkers(v)])
    );
  }
  if (rest.items) rest.items = stripMarkers(rest.items);
  return rest;
}

function toolResultPayload(content) {
  // Gemini wants an OBJECT response. Our tool results are JSON strings
  // (condenseToolResultForApi) — parse when possible, wrap otherwise.
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object') return parsed;
      return { result: parsed };
    } catch {
      return { result: content };
    }
  }
  if (content && typeof content === 'object') return content;
  return { result: content ?? null };
}

/**
 * Synthesized call id → the `thoughtSignature` Gemini returned with that call.
 *
 * Gemini 3.x rejects a history whose functionCall parts have lost their
 * signature ("Function call is missing a thought_signature in functionCall
 * parts"), so the FIRST tool call worked and the follow-up turn 400'd. The
 * signature is opaque, per-call, and must come back verbatim.
 *
 * It lives here rather than on the tool_use block because the block is
 * re-created from `{id, name, input}` on its way through the panel's
 * Anthropic-shaped history — any extra field is dropped in transit. The
 * synthesized id survives that trip, and this adapter is what mints it, so
 * the id is the one durable handle to hang the signature on.
 *
 * Bounded so a long-running session can't grow it without limit; eviction is
 * oldest-first, and a call whose signature has been evicted simply goes back
 * unsigned (degraded, exactly as it behaved before this map existed).
 */
const MAX_TRACKED_SIGNATURES = 500;
const signatureByCallId = new Map();

function rememberSignature(callId, signature) {
  if (!signature) return;
  signatureByCallId.set(callId, signature);
  while (signatureByCallId.size > MAX_TRACKED_SIGNATURES) {
    // Map preserves insertion order, so the first key is the oldest.
    signatureByCallId.delete(signatureByCallId.keys().next().value);
  }
}

/** Gemini puts the signature beside `functionCall` on the part; be tolerant. */
function readSignature(part) {
  return part?.thoughtSignature || part?.functionCall?.thoughtSignature || null;
}

/** Ids this adapter minted (see send()). Anything else came from another provider. */
const OWN_CALL_ID = /^gemini_call_/;

/**
 * Was this tool call made by a DIFFERENT provider earlier in the conversation?
 *
 * The model picker lets the user switch provider mid-conversation, and the
 * history is provider-agnostic by design — so a turn that Claude or GPT ran
 * arrives here carrying `toolu_…` / `call_…` ids. Those can never have a
 * Gemini thought signature, because Gemini never issued them, and an unsigned
 * functionCall part is rejected outright on 3.x. Replaying them as
 * functionCall parts turns "switch model mid-chat" into a dead conversation.
 */
const isForeignCall = (id) => Boolean(id) && !OWN_CALL_ID.test(String(id));

/**
 * Render a foreign provider's tool call as plain narration.
 *
 * Gemini requires signatures on functionCall parts specifically — text parts
 * carry no such requirement. Describing the call preserves what happened (the
 * model can see the work already done and not repeat it) while emitting
 * nothing that needs a signature it cannot have.
 */
function foreignCallText(block) {
  let args = '';
  try {
    args = JSON.stringify(block.input || {});
  } catch {
    args = '{}';
  }
  return `[earlier tool call, run on another model: ${block.name}(${args})]`;
}

export function toGeminiContents(messages) {
  // id → name map for functionResponse: Gemini has no call ids, so a
  // tool_result can only be answered by NAME — resolved from the assistant
  // tool_use block that carries the same id earlier in this same history.
  const nameById = {};
  for (const msg of messages || []) {
    if (msg.role === 'assistant' && Array.isArray(msg.content)) {
      for (const block of msg.content) {
        if (block.type === 'tool_use') nameById[block.id] = block.name;
      }
    }
  }

  const contents = [];
  for (const msg of messages || []) {
    const { role, content } = msg;
    if (typeof content === 'string') {
      contents.push({ role: role === 'assistant' ? 'model' : 'user', parts: [{ text: content }] });
      continue;
    }
    if (role === 'user') {
      const parts = (content || [])
        .filter((b) => b.type === 'tool_result')
        .map((b) => {
          // Its call was narrated as text (another provider ran it), so a
          // functionResponse here would answer a call Gemini cannot see.
          if (isForeignCall(b.tool_use_id)) {
            const name = nameById[b.tool_use_id] || 'tool';
            return { text: `[result of ${name}: ${JSON.stringify(toolResultPayload(b.content))}]` };
          }
          return {
            functionResponse: {
              name: nameById[b.tool_use_id] || 'unknown_tool',
              response: toolResultPayload(b.content),
            },
          };
        });
      if (parts.length > 0) contents.push({ role: 'user', parts });
      continue;
    }
    const parts = [];
    for (const block of content || []) {
      if (block.type === 'text' && block.text) parts.push({ text: block.text });
      if (block.type === 'tool_use') {
        if (isForeignCall(block.id)) {
          parts.push({ text: foreignCallText(block) });
          continue;
        }
        const part = { functionCall: { name: block.name, args: block.input || {} } };
        // Replay the signature Gemini issued for this exact call, or the next
        // turn is rejected outright on 3.x.
        const signature = signatureByCallId.get(block.id);
        if (signature) part.thoughtSignature = signature;
        parts.push(part);
      }
    }
    if (parts.length > 0) contents.push({ role: 'model', parts });
  }
  return contents;
}

let callCounter = 0;

/**
 * One streamed candidate chunk, handled exactly as the buffered path handles a
 * whole response.
 *
 * Captured live on 18.08.2026, and the shape is the opposite of the other two
 * providers: a `functionCall` arrives WHOLE, in one part, arguments included —
 * there is no fragmentation to accumulate and no per-call completion signal to
 * wait for, because a call is complete the moment it appears. Anthropic streams
 * a call's arguments as `input_json_delta` fragments closed by
 * `content_block_stop`; OpenAI streams them as `tool_calls` fragments keyed by
 * index and closed only by `finish_reason`. Assuming Gemini worked like either
 * would have added machinery that does nothing.
 *
 * The `thoughtSignature` still has to be captured here. Gemini 3.x rejects a
 * later turn whose functionCall parts have lost it, so a streamed call that
 * skipped rememberSignature would poison the next request rather than this one
 * — a failure that shows up one turn away from its cause.
 */
function handleGeminiChunk(chunk, { onTextDelta, onToolUse, onUsage }, state, mapFields) {
  if (chunk.usageMetadata) {
    // Cumulative on every chunk, so the last one wins rather than summing.
    onUsage?.({
      input_tokens: chunk.usageMetadata.promptTokenCount || 0,
      output_tokens: chunk.usageMetadata.candidatesTokenCount || 0,
    });
  }

  const candidate = (chunk.candidates || [])[0];
  if (!candidate) return;

  for (const part of candidate.content?.parts || []) {
    if (part.text) onTextDelta?.(part.text);
    if (part.functionCall) {
      state.sawFunctionCall = true;
      callCounter += 1;
      const callId = `gemini_call_${Date.now()}_${callCounter}`;
      rememberSignature(callId, readSignature(part));
      onToolUse?.({
        id: callId,
        name: part.functionCall.name,
        input: coerceOpenMapArgs(part.functionCall.name, part.functionCall.args || {}, mapFields),
      });
    }
  }

  if (candidate.finishReason) state.finishReason = candidate.finishReason;
}

/** Whether this adapter can stream in the current runtime. */
export function supportsStreaming() {
  return canStream();
}

/**
 * Stream a turn.
 *
 * Same endpoint family as `send`, with `:streamGenerateContent?alt=sse`. The
 * per-chunk handling is shared with the buffered path so the two cannot drift
 * on the thing that matters — signature capture and open-map coercion.
 */
export async function sendStreaming({
  apiKey, model, system, reference, tools, messages,
  onTextDelta, onToolUse, onUsage, onStart,
}) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const mapFields = mapFieldsFor(tools);
  const body = {
    systemInstruction: { parts: [{ text: fullSystem }] },
    tools: [{ functionDeclarations: toFunctionDeclarations(tools) }],
    contents: toGeminiContents(messages),
  };

  const state = { sawFunctionCall: false, finishReason: null };

  await streamProviderSse(streamUrl(model), headers(apiKey), body, (payload) => {
    if (!payload) return;
    let chunk;
    try {
      chunk = JSON.parse(payload);
    } catch {
      // One malformed frame must not end an otherwise healthy turn.
      return;
    }
    handleGeminiChunk(chunk, { onTextDelta, onToolUse, onUsage }, state, mapFields);
  }, { onStart });

  return {
    stopReason: state.sawFunctionCall
      ? 'tool_use'
      : state.finishReason === 'MAX_TOKENS' ? 'max_tokens' : 'end_turn',
  };
}

export async function send({ apiKey, model, system, reference, tools, messages, onTextDelta, onToolUse, onUsage }) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const mapFields = mapFieldsFor(tools);
  const body = {
    systemInstruction: { parts: [{ text: fullSystem }] },
    tools: [{ functionDeclarations: toFunctionDeclarations(tools) }],
    contents: toGeminiContents(messages),
  };

  const { status, json } = await postProviderJson(url(model), headers(apiKey), body);
  if (status >= 400 || !json) {
    throw providerError('Gemini', status, json?.error?.message);
  }

  if (json.usageMetadata) {
    onUsage?.({
      input_tokens: json.usageMetadata.promptTokenCount || 0,
      output_tokens: json.usageMetadata.candidatesTokenCount || 0,
    });
  }
  const candidate = (json.candidates || [])[0] || {};
  const parts = candidate.content?.parts || [];
  let sawFunctionCall = false;
  for (const part of parts) {
    if (part.text) onTextDelta?.(part.text);
    if (part.functionCall) {
      sawFunctionCall = true;
      callCounter += 1;
      // Synthesized: see the module doc. Unique within this client session.
      const callId = `gemini_call_${Date.now()}_${callCounter}`;
      rememberSignature(callId, readSignature(part));
      onToolUse?.({
        id: callId,
        name: part.functionCall.name,
        // Fields declared as JSON strings for Gemini come back as strings —
        // turn them into the objects every executor expects.
        input: coerceOpenMapArgs(part.functionCall.name, part.functionCall.args || {}, mapFields),
      });
    }
  }
  const stopReason = sawFunctionCall
    ? 'tool_use'
    : candidate.finishReason === 'MAX_TOKENS'
      ? 'max_tokens'
      : 'end_turn';
  return { stopReason };
}

/**
 * Validate the key (and that it can see this model) by READING the model back
 * — same reasoning as the OpenAI adapter: Gemini 3.x are thinking models, so
 * a `maxOutputTokens: 1` ping spends its whole budget on thinking and fails
 * on a valid key. GET /models/{model} also gives the clearest possible error
 * for the OTHER failure the model list keeps producing: a retired id answers
 * 404 naming its replacement.
 */
export async function testKey({ apiKey, model }) {
  const modelUrl = `${API_BASE}/${encodeURIComponent(model)}`;
  const { status, json } = await getProviderJson(modelUrl, headers(apiKey));
  if (status >= 400 || !json) {
    throw providerError('Gemini', status, json?.error?.message);
  }
  return true;
}
